/* ===== CONTENTFUL CONTENT, USING OUR "CONTENT API" ======================== */

import { log, warn } from '../init/log'

import type {
  ContentfulLocalizedAsset,
  ContentfulLocalizedEntry,
  TypeDishListSkeleton,
  TypeDishSkeleton,
  TypeListSkeleton,
  TypePageSkeleton,
  TypePrefilledCartSkeleton,
  TypeTranslationsRichTextSkeleton,
  TypeTranslationsSkeleton,
  TypeBoxListSkeleton,
} from '@juitnow/api-content'
import type { Entry } from 'contentful'
import type { Locale } from '.'

type DownloadedContent = {
  assets: ContentfulLocalizedAsset[],
  entries: ContentfulLocalizedEntry[],
}

type AllContent = {
  /** All assets, directly from Contentful */
  assets: ContentfulLocalizedAsset[],
  /** All entries, directly from Contentful (all links resolved) */
  entries: ContentfulLocalizedEntry[],
  /** All pages, keyed by slug */
  pages: Record<string, Entry<TypePageSkeleton, undefined, Locale>>
  /** All lists, keyed by slug */
  lists: Record<string, Entry<TypeListSkeleton, undefined, Locale>>
  /** All dishes, keyed by slug (or EAN, EAN can be a slug, too) */
  dishesBySlug: Record<string, Entry<TypeDishSkeleton, undefined, Locale>>
  /** All dishes, uniquely keyed by EAN */
  allDishes: Entry<TypeDishSkeleton, undefined, Locale>[]
  /** All dish lists, keyed by title */
  dishLists: Record<string, Entry<TypeDishListSkeleton, undefined, Locale>>
  /** All prefilled carts, keyed by slug */
  prefilledCarts: Record<string, Entry<TypePrefilledCartSkeleton, undefined, Locale>>
  /** All box lists */
  boxLists: Entry<TypeBoxListSkeleton, undefined, Locale>[]
  /** All translations */
  translations: Entry<TypeTranslationsSkeleton, undefined, Locale>[]
  /** All rich-text translations */
  translationsRichText: Entry<TypeTranslationsRichTextSkeleton, undefined, Locale>[]
}

const _allPromise: { [ locale in Locale ]?: Promise<AllContent> } = {}
function getContent(locale: Locale): Promise<AllContent> {
  if (_allPromise[locale]) return _allPromise[locale]

  const now = Date.now()
  const url = new URL(`/content-${locale}.json`, import.meta.env.VITE_CDN_URL)
  const promise = _allPromise[locale] = fetch(url)
      .then((response) => response.json() as Promise<DownloadedContent>)
      .then(async ({ entries, assets }) => {
        const entriesById = Object.fromEntries(entries.map((entry) => [ entry.sys.id, entry ]))
        const assetsById = Object.fromEntries(assets.map((asset) => [ asset.sys.id, asset ]))

        let linkedEntries = 0
        let linkedAssets = 0

        const pages: Record<string, Entry<TypePageSkeleton, undefined, Locale>> = {}
        const lists: Record<string, Entry<TypeListSkeleton, undefined, Locale>> = {}
        const dishesByEan: Record<string, Entry<TypeDishSkeleton, undefined, Locale>> = {}
        const dishesBySlug: Record<string, Entry<TypeDishSkeleton, undefined, Locale>> = {}
        const dishLists: Record<string, Entry<TypeDishListSkeleton, undefined, Locale>> = {}
        const prefilledCarts: Record<string, Entry<TypePrefilledCartSkeleton, undefined, Locale>> = {}
        const boxLists: Entry<TypeBoxListSkeleton, undefined, Locale>[] = []
        const translations: Entry<TypeTranslationsSkeleton, undefined, Locale>[] = []
        const translationsRichText: Entry<TypeTranslationsRichTextSkeleton, undefined, Locale>[] = []

        for (const entry of entries) {
          if (entry.sys.contentType.sys.id === 'page') {
            const page = entry as Entry<TypePageSkeleton, undefined, Locale>
            pages[page.fields.slug] = page
          } else if (entry.sys.contentType.sys.id === 'list') {
            const list = entry as Entry<TypeListSkeleton, undefined, Locale>
            lists[list.fields.slug] = list
          } else if (entry.sys.contentType.sys.id === 'dish') {
            const dish = entry as Entry<TypeDishSkeleton, undefined, Locale>
            dishesBySlug[dish.fields.slug] = dish
            dishesBySlug[dish.fields.ean] = dish
            dishesByEan[dish.fields.ean] = dish
          } else if (entry.sys.contentType.sys.id === 'dishList') {
            const dishList = entry as Entry<TypeDishListSkeleton, undefined, Locale>
            dishLists[dishList.fields.title] = dishList
          } else if (entry.sys.contentType.sys.id === 'prefilledCart') {
            const prefilledCart = entry as Entry<TypePrefilledCartSkeleton, undefined, Locale>
            prefilledCarts[prefilledCart.fields.slug] = prefilledCart
          } else if (entry.sys.contentType.sys.id === 'boxList') {
            const boxList = entry as Entry<TypeBoxListSkeleton, undefined, Locale>
            boxLists.push(boxList)
          } else if (entry.sys.contentType.sys.id === 'translations') {
            const translation = entry as Entry<TypeTranslationsSkeleton, undefined, Locale>
            translations.push(translation)
          } else if (entry.sys.contentType.sys.id === 'translationsRichText') {
            const translationRichText = entry as Entry<TypeTranslationsRichTextSkeleton, undefined, Locale>
            translationsRichText.push(translationRichText)
          }

          for (const [ key, value ] of Object.entries(entry.fields)) {
            if (typeof value !== 'object') continue
            if (! value) continue

            if (Array.isArray(value)) {
              for (let i = 0; i < value.length; i++) {
                const anyItem = value[i] as any

                if (anyItem?.sys?.['type'] === 'Link' &&
                    anyItem?.sys?.linkType === 'Entry' &&
                    anyItem?.sys?.id) {
                  const linked = entriesById[anyItem.sys.id]
                  if (linked) linkedEntries++
                  if (linked) value[i] = linked as any
                  else warn(`Missing linked entry ${anyItem.sys.id} linked from ${entry.sys.id}`)
                }

                if (anyItem?.sys?.['type'] === 'Link' &&
                    anyItem?.sys?.linkType === 'Asset' &&
                    anyItem?.sys?.id) {
                  const linked = assetsById[anyItem.sys.id]
                  if (linked) linkedAssets++
                  if (linked) value[i] = linked as any
                  else warn(`Missing linked asset ${anyItem.sys.id} linked from ${entry.sys.id}`)
                }
              }
            } else {
              const anyValue = value as any
              if (anyValue?.sys?.['type'] === 'Link' &&
                  anyValue?.sys?.linkType === 'Entry'&&
                  anyValue?.sys?.id) {
                const linked = entriesById[anyValue.sys.id]
                if (linked) linkedEntries++
                if (linked) entry.fields[key] = linked as any
                else warn(`Missing linked entry ${anyValue.sys.id} linked from ${entry.sys.id}`)
              }

              if (anyValue?.sys?.['type'] === 'Link' &&
                  anyValue?.sys?.linkType === 'Asset'&&
                  anyValue?.sys?.id) {
                const linked = assetsById[anyValue.sys.id]
                if (linked) linkedAssets++
                if (linked) entry.fields[key] = linked as any
                else warn(`Missing linked asset ${anyValue.sys.id} linked from ${entry.sys.id}`)
              }
            }
          }
        }

        const allDishes = Object.values(dishesByEan)

        const time = Date.now() - now
        log('Contentful content processed', linkedEntries, 'linked entries and', linkedAssets, 'linked assets in', time, 'ms')
        return { entries, assets, pages, lists, dishesBySlug, allDishes, dishLists, prefilledCarts, boxLists, translations, translationsRichText }
      })

      .catch((error) => {
        if (_allPromise[locale] === promise) delete _allPromise[locale]
        throw error
      })

  return _allPromise[locale]
}

export async function getPage(locale: Locale, slug: string): Promise<Entry<TypePageSkeleton, undefined, Locale> | undefined> {
  const { pages } = await getContent(locale)
  return pages[slug]
}

export async function getList(locale: Locale, slug: string): Promise<Entry<TypeListSkeleton, undefined, Locale> | undefined> {
  const { lists } = await getContent(locale)
  return lists[slug]
}

export async function getDish(locale: Locale, slugOrEan: string): Promise<Entry<TypeDishSkeleton, undefined, Locale> | undefined> {
  const { dishesBySlug } = await getContent(locale)
  return dishesBySlug[slugOrEan]
}

export async function getDishList(locale: Locale, title: string): Promise<Entry<TypeDishListSkeleton, undefined, Locale> | undefined> {
  const { dishLists } = await getContent(locale)
  return dishLists[title]
}

export async function getPrefilledCart(locale: Locale, slug: string): Promise<Entry<TypePrefilledCartSkeleton, undefined, Locale> | undefined> {
  const { prefilledCarts } = await getContent(locale)
  return prefilledCarts[slug]
}

export async function getDishes(locale: Locale): Promise<Entry<TypeDishSkeleton, undefined, Locale>[]> {
  const { allDishes } = await getContent(locale)
  return allDishes
}

export async function getBoxLists(locale: Locale): Promise<Entry<TypeBoxListSkeleton, undefined, Locale>[]> {
  const { boxLists } = await getContent(locale)
  return boxLists
}

export async function getTranslations(locale: Locale): Promise<Entry<TypeTranslationsSkeleton, undefined, Locale>[]> {
  const { translations } = await getContent(locale)
  return translations
}

export async function getTranslationsRichText(locale: Locale): Promise<Entry<TypeTranslationsRichTextSkeleton, undefined, Locale>[]> {
  const { translationsRichText } = await getContent(locale)
  return translationsRichText
}

/* Upon initialization, fetch the content for the current locale */
const match = window.location.pathname.match(/^\/(en|de)(\/|$)/i)
if (match) getContent(match[1].toLowerCase() as Locale)
